import React, { useState, useRef, ReactNode } from 'react';
import { Nullable } from 'global';
import { Info } from '@mui/icons-material';
import { Typography, Button, Stack, Collapse } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

type ViewMode = 'html' | 'text';

interface Props {
  text: Nullable<string>;
  html: Nullable<string>;
  defaultViewMode?: ViewMode;
  isSupportSwitchMode?: boolean;
}

export const EmailViewer = ({ text, html, defaultViewMode = 'html', isSupportSwitchMode = false }: Props) => {
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [expanded, setExpanded] = useState<boolean>(false);

  const ref = useRef<HTMLDivElement>(null);

  const contentByType: Record<ViewMode, string | null> = { html, text };
  const content = contentByType[viewMode];

  const renderByMode: Record<ViewMode, ReactNode> = {
    html: (
      <RenderWithCondition
        condition={!!content}
        fallBack={
          <Stack direction="row" gap="0.5rem" alignItems="center" justifyContent="center">
            <Info color="info" />

            <Typography ref={ref} fontWeight={500} color="info" sx={{ whiteSpace: 'pre-wrap' }}>
              No data
            </Typography>
          </Stack>
        }
      >
        <div dangerouslySetInnerHTML={{ __html: content! }} />
      </RenderWithCondition>
    ),
    text: (
      <Typography ref={ref} sx={{ whiteSpace: 'pre-wrap' }}>
        {content}
      </Typography>
    ),
  };

  return (
    <Stack alignItems="center">
      <RenderWithCondition condition={isSupportSwitchMode && !!html && !!text && expanded}>
        <Button
          size="small"
          variant="contained"
          sx={{ alignSelf: 'flex-end' }}
          onClick={() => setViewMode(prev => (prev === 'html' ? 'text' : 'html'))}
        >
          Switch view mode
        </Button>
      </RenderWithCondition>

      <Collapse
        in={expanded}
        collapsedSize="4rem"
        sx={{ width: '100%', ...(Boolean(content) ? {} : { display: 'flex', alignItems: 'center' }) }}
      >
        {renderByMode[viewMode]}
      </Collapse>

      <RenderWithCondition condition={Boolean(content)}>
        <Button variant="text" size="small" onClick={() => setExpanded(prev => !prev)}>
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      </RenderWithCondition>
    </Stack>
  );
};
